import { AHText, AHWidgetNoData, AHWidgetTitle, Box } from "arkh";
import React, { useMemo } from "react";
import { BaseWidgetProps } from "../BaseProps";
import { WidgetTaskSingleCheck } from "./CheckTask/WidgetTaskSingleCheck";
import { WidgetTaskSingleNumber } from "./NumberTask/WidgetTaskSingleNumber";
import { useWidgetTaskSingleSubscription } from "./useWidgetTaskSingleSubscription";
import { useEvent } from "../useEvent";
import { useWidgetSections } from "./useWidgetTaskSingleSections";
import { graphql, useFragment } from "react-relay";
import {
  WidgetTaskSingleSize,
  WidgetTaskSingle_widgetTaskSingle$key,
} from "./__generated__/WidgetTaskSingle_widgetTaskSingle.graphql";
import { useSetWidgetFocus } from "../useSetFocus";

export const WidgetTaskSingle = ({
  widget: widgetKey,
  onEvent,
  interactive,
  isFocused,
  storecastId,
  mode,
  maxOccasions,
  maxParts,
  clientFilterSectionIds,
  clientForceSize,
}: {
  widget: WidgetTaskSingle_widgetTaskSingle$key;
  maxOccasions?: number;
  maxParts?: number;
  clientFilterSectionIds?: string[]; // used to filter out sections when showing preview
  clientForceSize?: WidgetTaskSingleSize;
} & BaseWidgetProps) => {
  const widget = useFragment(
    graphql`
      fragment WidgetTaskSingle_widgetTaskSingle on WidgetTaskSingle {
        id
        title
        grouping
        taskSingleSize
        ...useWidgetTaskSingleSections_widgetTaskSingle
        activeSectionVersions {
          section {
            id
          }
        }
        task {
          id
          routine {
            id
            type
            title
          }
          __typename

          ... on TaskNumber {
            ...WidgetTaskSingleNumberOccasionPage_taskNumber
          }
          ... on TaskCheck {
            ...WidgetTaskSingleCheck_taskCheck
          }
        }
      }
    `,
    widgetKey
  );

  useWidgetTaskSingleSubscription(
    widget.task?.routine.id,
    widget.task?.id,
    interactive === false
  );

  const setFocus = useSetWidgetFocus(widget.id, storecastId);
  const onPress = useEvent((sequenceNumbers: number[]) => {
    if (mode === "FOCUS" && !isFocused) {
      setFocus();
    } else {
      if (
        widget.task?.__typename === "TaskNumber" &&
        widget.task?.routine.type === "KPI" &&
        sequenceNumbers.length > 0
      ) {
        const inProgress = Math.max(...sequenceNumbers);
        onEvent?.({
          kind: "WidgetTaskSingleKPI",
          routineId: widget.task.routine.id,
          sequenceNumber: inProgress,
          widgetId: widget.id,
          sectionIds: widget.activeSectionVersions.map((s) => s.section.id),
          routineTitle: widget.task.routine.title,
        });
      } else {
        const task = widget.task;
        if (task?.id) {
          onEvent?.({
            kind: "WidgetTaskSingle",
            widgetId: widget.id,
            routineId: task.routine.id,
            sequenceNumber: sequenceNumbers[0],
            taskId: task.id,
          });
        }
      }
    }
  });

  const sections = useWidgetSections(widget, clientFilterSectionIds);

  const size = clientForceSize ?? widget.taskSingleSize;
  const body = useMemo(() => {
    if (!widget.task) {
      return (
        <Box color="surface" style={{ flex: 1 }}>
          <AHWidgetTitle color="darkerText">{widget.title}</AHWidgetTitle>
          <Box spaceHorizontal="small">
            <AHText type="detail" numberOfLines={1}>
              {widget.title}
            </AHText>
          </Box>
          <AHWidgetNoData />
        </Box>
      );
    }

    switch (widget.task?.__typename) {
      case "TaskNumber": {
        return (
          <WidgetTaskSingleNumber
            grouping={widget.grouping ?? "SECTION"}
            task={widget.task}
            large={size === "TWO_X_ONE"}
            onPress={onPress}
            sections={sections}
            maxOccasions={maxOccasions}
            maxParts={maxParts}
            interactive={interactive}
          />
        );
      }
      case "TaskCheck": {
        return (
          <WidgetTaskSingleCheck
            task={widget.task}
            large={size === "TWO_X_ONE"}
            onPress={onPress}
            sections={sections}
            maxOccasions={maxOccasions}
            maxParts={maxParts}
          />
        );
      }

      default:
        return null;
    }
  }, [
    interactive,
    maxOccasions,
    maxParts,
    onPress,
    sections,
    size,
    widget.grouping,
    widget.task,
    widget.title,
  ]);

  return body;
};
